import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Zap, Lock, Smartphone } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { toolCategories } from '../data/tools';
import { ToolCard } from '../components/Tools/ToolCard';
import { AdSense } from '../components/Layout/AdSense';
import { InFeedAd } from '../components/Layout/InFeedAd';
import { InArticleAd } from '../components/Layout/InArticleAd';
import { DisplayAd } from '../components/Layout/DisplayAd';

export const HomePage: React.FC = () => {
  const featuredTools = [
    ...toolCategories[0].tools.slice(0, 2), // Image tools
    ...toolCategories[1].tools.slice(0, 2), // PDF tools
    ...toolCategories[2].tools.slice(0, 2), // Text tools
  ];

  const features = [
    {
      icon: Shield,
      title: 'Privacy First',
      description: 'All processing happens locally in your browser. No uploads, no storage, no tracking.'
    },
    {
      icon: Zap,
      title: 'Lightning Fast',
      description: 'Instant processing with no server delays. Your files never leave your device.'
    },
    {
      icon: Lock,
      title: 'Secure by Design',
      description: 'Client-side processing ensures your sensitive data stays completely private.'
    },
    {
      icon: Smartphone,
      title: 'Works Everywhere',
      description: 'Responsive design that works perfectly on desktop, tablet, and mobile devices.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6">
              40+ Powerful Tools
              <span className="block text-blue-600 dark:text-blue-400">100% Private</span>
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-8 max-w-3xl mx-auto">
              Process images, PDFs, text, and more - all in your browser. No uploads, no storage, 
              no privacy concerns. Your data never leaves your device.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/category/image"
                className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
              >
                Explore Tools
              </Link>
              <Link
                to="/privacy"
                className="px-8 py-3 border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300 font-semibold rounded-lg transition-colors"
              >
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Top Display Ad */}
      <DisplayAd className="max-w-4xl mx-auto" />

      {/* Features Section */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Why Choose PrivateTools?
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Built with privacy and security in mind, our tools ensure your data stays completely private.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* First In-feed Ad - After Features */}
      <InFeedAd className="max-w-4xl mx-auto" />

      {/* Featured Tools */}
      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Popular Tools
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Try some of our most popular privacy-first online tools
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {featuredTools.map((tool) => (
              <ToolCard key={tool.id} tool={tool} />
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/category/image"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors"
            >
              View All Tools
            </Link>
          </div>
        </div>
      </section>

      {/* Second In-feed Ad - After Featured Tools */}
      <InFeedAd className="max-w-4xl mx-auto" />

      {/* In-article Ad Section */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Privacy-First Processing
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
              Every tool on PrivateTools processes your data locally in your browser. 
              This means your files, images, and documents never leave your device.
            </p>
          </div>
          
          {/* In-article Ad */}
          <InArticleAd />
          
          <div className="text-center mt-8">
            <p className="text-gray-600 dark:text-gray-400">
              Our client-side processing approach ensures maximum privacy and security 
              while providing powerful functionality for all your needs.
            </p>
          </div>
        </div>
      </section>

      {/* Categories Overview */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Tool Categories
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Discover tools organized by category
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {toolCategories.map((category) => {
              const IconComponent = (LucideIcons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[category.icon];
              return (
                <Link
                  key={category.id}
                  to={`/category/${category.id}`}
                  className="block p-6 bg-white dark:bg-gray-800 rounded-xl hover:shadow-lg transition-all duration-200 group border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 hover-lift"
                >
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center group-hover:bg-blue-200 dark:group-hover:bg-blue-800/40 transition-colors">
                      <IconComponent className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                        {category.name}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {category.tools.length} tools
                      </p>
                    </div>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400">
                    {category.description}
                  </p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Third In-feed Ad - After Categories */}
      <InFeedAd className="max-w-4xl mx-auto" />

      {/* Bottom Display Ad */}
      <DisplayAd className="max-w-4xl mx-auto" />
    </div>
  );
};